import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import Modal from "../Modal";
import Btn from "../Btn";
import { todoOutsidePartyApi } from "../../api/todoOutsidePartyApi";
import { Field, errMsg, inputStyle, unwrap } from "./todoShared";

/**
 * Add or change one OUTSIDE PERSON (to-do). Only the name is required; the
 * place shows with the name on tasks ("Raju Tailor, Akkalkot Road"), the
 * mobile on the task to call them.
 *
 * `person` null = new (`initialName` fills the name, e.g. what was typed in
 * the picker). Calls onSaved(person) with the saved one. Opens above the
 * task form (zIndex).
 */
export default function OutsidePersonModal({ open, person, initialName = "", onClose, onSaved, zIndex = 1100 }) {
  const editing = Boolean(person?.id);
  const [form, setForm] = useState({ name: "", mobile: "", place: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({
      name: person?.name || initialName || "",
      mobile: person?.mobile || "",
      place: person?.place || "",
      notes: person?.notes || "",
    });
    setError("");
  }, [open, person, initialName]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => { if (!saving) onClose(); };

  const save = async () => {
    if (!form.name.trim()) { setError("Enter the name"); return; }
    const digits = form.mobile.replace(/[\s\-()]/g, "");
    if (digits && !/^\+?\d{10,13}$/.test(digits)) { setError("Enter a valid mobile number (10 digits)"); return; }
    setSaving(true);
    setError("");
    const body = {
      name: form.name.trim(),
      mobile: digits || null,
      place: form.place.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      const res = editing ? await todoOutsidePartyApi.update(person.id, body) : await todoOutsidePartyApi.create(body);
      window.__garmentToast?.success?.(res?.data?.message || "Saved");
      onSaved?.(unwrap(res));
    } catch (e) {
      setError(errMsg(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={close} width={480} zIndex={zIndex}
      title={editing ? `Edit ${person.name}` : "New outside person"}
      subtitle="Someone outside the factory who does to-do work — they have no login"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="secondary" onClick={close} disabled={saving}>Cancel</Btn>
          <Btn icon={<Check size={13} />} onClick={save} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add person"}
          </Btn>
        </div>
      }>
      <Field label="Name *">
        <input autoFocus value={form.name} maxLength={150} onChange={set("name")}
          placeholder="e.g. Raju Tailor" style={inputStyle} />
      </Field>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 160px" }}>
          <Field label="Mobile">
            <input value={form.mobile} maxLength={20} inputMode="tel" onChange={set("mobile")}
              placeholder="10-digit mobile" style={inputStyle} />
          </Field>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <Field label="Place" hint={editing && person.openTasks > 0
            ? `A new name or place also shows on their ${person.openTasks} open task${person.openTasks === 1 ? "" : "s"}.`
            : "Shown with the name on tasks"}>
            <input value={form.place} maxLength={140} onChange={set("place")}
              placeholder="e.g. Akkalkot Road" style={inputStyle} />
          </Field>
        </div>
      </div>
      <Field label="Note" last>
        <textarea value={form.notes} maxLength={500} rows={2} onChange={set("notes")}
          placeholder="Optional — what they do, rates, timings…" style={{ ...inputStyle, resize: "vertical" }} />
      </Field>
      {error && (
        <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "var(--danger-bg)", color: "var(--danger)", fontSize: 13 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
